import { type Address } from '@dhl/components'
import { type PieceLocationType } from './PieceLocationType.js'
import { type MomentPieceTimeIndication, type PieceTimeIndication } from './PieceTimeIndication.js'

export type PieceType = 'incoming' | 'outgoing'

export type Sender = {
  name?: string
  address?: Partial<Address>
}

export type PieceLocation = {
  name?: string
  contactName?: string
  locationType?: PieceLocationType
  address?: Partial<Address>
  email?: string
  profileAddressName?: string
  isServicePointOrTerminal?: boolean
  isDHLDeliveryPoint?: boolean
}

export type Piece = {
  orderId?: string
  pieceId: string
  type: PieceType
  barcode: string
  destination?: PieceLocation
  receiver?: PieceLocation
  sender?: Sender
  timeCreated?: string
  receivingTimeIndication?: PieceTimeIndication
  status: string | null
  category?: string
  intervenable: boolean | null
  intervenableBarcode: string | null
  totalPrice: Price | null
  receivedDaysAgo: number | null | undefined
  returnedDaysAgo: number | null | undefined
  isLockerDelivery?: boolean
  isRegistered: boolean
  isReturn: boolean
  printlessCode?: string | null
  identifier: string
  isClimateNeutralDelivery?: boolean
  isUndeliverable: boolean
  undeliverableSinceDaysAgo: number | null | undefined
  returnable: boolean | null
  hasDeliveryCode: boolean | undefined | null
  hasServicePointPickupCode: boolean | undefined | null
  hasLockerCode: boolean | undefined | null
  source?: 'MDP' | 'C2C'
}

export type PieceWithoutId = Omit<Piece, 'pieceId'>

export type Price = {
  withTax: number
  withoutTax: number
  currency?: string
}

export type ExtraPiece = {
  barcode: string
  postalCode: string
}

const ignoreInSort = (timeIndication?: PieceTimeIndication): boolean =>
  !timeIndication || timeIndication.indicationType === 'IntervalIndication' || timeIndication.moment === ''

export const sortByMomentIndication: (a: Piece, b: Piece) => number = (
  { receivingTimeIndication: timeA }: Piece,
  { receivingTimeIndication: timeB }: Piece
): number => {
  const ignoreA = ignoreInSort(timeA)
  const ignoreB = ignoreInSort(timeB)

  if (ignoreA && ignoreB) {
    return 0
  }
  if (ignoreA) {
    return 1
  }
  if (ignoreB) {
    return -1
  }

  return (
    new Date((timeB as MomentPieceTimeIndication).moment).getTime() -
    new Date((timeA as MomentPieceTimeIndication).moment).getTime()
  )
}

export const sortByCreationDate: (a: Piece, b: Piece) => number = (
  { timeCreated: timeA }: Piece,
  { timeCreated: timeB }: Piece
): number => {
  if (timeA && timeB) {
    return new Date(timeA).getTime() - new Date(timeB).getTime()
  }
  if (!timeA && !timeB) {
    return 0
  }
  if (!timeB) {
    return -1
  }
  return 1
}

export const sortByReturnedDaysAgo: (a: Piece, b: Piece) => number = (
  { returnedDaysAgo: daysA }: Piece,
  { returnedDaysAgo: daysB }: Piece
): number => (daysA || 0) - (daysB || 0)

export const sortByUndeliverableSinceDaysAgo: (a: Piece, b: Piece) => number = (
  { undeliverableSinceDaysAgo: daysA }: Piece,
  { undeliverableSinceDaysAgo: daysB }: Piece
): number => (daysA || 0) - (daysB || 0)

export const sortByUndeliverableAndReturned: (a: Piece, b: Piece) => number = (
  { returnedDaysAgo: daysA1, undeliverableSinceDaysAgo: daysA2 }: Piece,
  { returnedDaysAgo: daysB1, undeliverableSinceDaysAgo: daysB2 }: Piece
): number => (daysA1 || 0) + (daysA2 || 0) - (daysB1 || 0) - (daysB2 || 0)
